import React, { useEffect, useState } from 'react';
import { FiAlertTriangle, FiBriefcase } from 'react-icons/fi';
import { onGameEvent } from '../../core/GameEvents';
import {
  PALETTE,
  Card,
  ErrText,
  Hint,
  SectionTitle,
  StationBody,
  StationHeader,
  dangerBtnStyle,
  primaryBtnStyle,
  stationContainerStyle,
} from './stationTheme';
import {
  CreateCorpForm,
  DissolveCorpForm,
  useCorporationData,
} from '../corp';

interface Props {
  stationId: string;
  onBack: () => void;
}

type Mode = 'paperwork' | 'create' | 'dissolve';

/**
 * The Corporation Office is the trámite-only space: founding and dissolving
 * a corporation are notarial acts and must happen in person at a station.
 * Day-to-day operations (withdrawals, members, distributions, votes,
 * permissions) live in the always-on Corporation HUD panel (top-right).
 */
export function CorporationPanel({ stationId, onBack }: Props) {
  void stationId;
  const [mode, setMode] = useState<Mode>('paperwork');
  const [credits, setCredits] = useState(0);
  const [flash, setFlash] = useState<string | null>(null);

  const data = useCorporationData({
    autoFetch: true, // Need the managed-corps list to enable dissolve.
    onCreated: () => setMode('paperwork'),
    onDissolved: (info) => {
      setMode('paperwork');
      const refundNote = info.refundedCredits > 0
        ? ` Refunded ${info.refundedCredits.toLocaleString()} CR to your wallet.`
        : '';
      setFlash(`${info.corpName} [${info.corpTag}] dissolved.${refundNote}`);
    },
  });

  useEffect(() => onGameEvent('playerStateUpdate', (s) => setCredits(s.credits)), []);

  const managedCorps = (data.corps ?? []).filter((c) => c.isManager);

  if (mode === 'create') {
    return (
      <div style={stationContainerStyle}>
        <StationHeader
          onBack={() => { setMode('paperwork'); data.clearError(); }}
          title="FOUND A CORPORATION"
          hint="[ESC] Back"
        />
        <StationBody>
          <CreateCorpForm
            credits={credits}
            busy={data.busy}
            error={data.error}
            setError={data.setError}
            onSubmit={(payload) => data.createContract(payload)}
          />
        </StationBody>
      </div>
    );
  }

  if (mode === 'dissolve') {
    return (
      <div style={stationContainerStyle}>
        <StationHeader
          onBack={() => { setMode('paperwork'); data.clearError(); }}
          title="DISSOLVE A CORPORATION"
          hint="[ESC] Back"
        />
        <StationBody>
          <DissolveCorpForm
            managedCorps={managedCorps}
            busy={data.busy}
            error={data.error}
            onDissolve={(corpId) => data.dissolve({ corpId })}
            onCancel={() => { setMode('paperwork'); data.clearError(); }}
          />
        </StationBody>
      </div>
    );
  }

  return (
    <div style={stationContainerStyle}>
      <StationHeader onBack={onBack} title="CORPORATION OFFICE" hint="[ESC] Return to Station" />
      <StationBody>
        <Card>
          <SectionTitle>Found a corporation</SectionTitle>
          <Hint>
            A corporation is a legal entity that can own stations, buildings and hold credits,
            separate from any pilot. Founding one is a notarial act and must be done in person here.
          </Hint>
          <button style={primaryBtnStyle(false)} onClick={() => { setMode('create'); data.clearError(); }}>
            <FiBriefcase size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
            FOUND A CORPORATION
          </button>
        </Card>

        {managedCorps.length > 0 && (
          <Card>
            <SectionTitle>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <FiAlertTriangle size={12} style={{ color: PALETTE.RED }} />
                Dissolve a corporation
              </span>
            </SectionTitle>
            <Hint>
              Permanently shut down a corp you manage. Refunds the bank balance to your wallet.
              Refuses if the corp still owns stations or has an active manager-removal vote.
            </Hint>
            <button style={dangerBtnStyle(false)} onClick={() => { setMode('dissolve'); data.clearError(); }}>
              <FiAlertTriangle size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              DISSOLVE A CORPORATION
            </button>
          </Card>
        )}

        <Card>
          <SectionTitle>Manage existing corporations</SectionTitle>
          <Hint style={{ color: PALETTE.DIM }}>
            Day-to-day operations — withdrawals, members, distributions, votes, permissions — are handled
            from the Corporation panel (briefcase icon, top-right of the screen, hotkey O). You can use
            it anywhere in space.
          </Hint>
        </Card>

        {flash && (
          <div style={{ color: PALETTE.GREEN, fontSize: 12, padding: '4px 0' }}>{flash}</div>
        )}
        {data.error && <ErrText>{data.error}</ErrText>}
      </StationBody>
    </div>
  );
}

export default CorporationPanel;
